// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import React from "react";

interface Message {
    key: string;
    type: "success" | "danger" | "info" | "warning";
    message: any;
    closeable?: boolean;
}

interface CartPageContainerMessageContextType {
    messages: Message[];
    addMessage: (message: Message) => void;
    removeMessage: (messageKey: string) => void;
    flushMessages: () => void;
}

const CartPageContainerMessagesContext = React.createContext<CartPageContainerMessageContextType>({
    messages: [],
    addMessage: (message: Message) => {},
    removeMessage: (messageKey: string) => {},
    flushMessages: () => {},
});

export const CartPageContainerMessagesContextWrapper: React.FC = ({ children }) => {
    const [messages, setMessages] = React.useState<Message[]>([]);
    const messagesCache: Message[] = [];

    const addMessage = (message: Message) => {
        const hasMessage = messagesCache.find(m => m.key === message.key);
        if (hasMessage) {
            return;
        }
        messagesCache.push(message);
        setMessages(messagesCache);
    };

    const removeMessage = (messageKey: string) => {
        const hasMessage = messagesCache.find(m => m.key === messageKey);
        if (!hasMessage) {
            return;
        }
        setMessages(messagesCache.filter(e => e.key !== messageKey));
    };

    const flushMessages = () => {
        setMessages([]);
        messagesCache.length = 0;
    };

    return (
        <CartPageContainerMessagesContext.Provider
            value={{
                messages,
                addMessage,
                removeMessage,
                flushMessages,
            }}
        >
            {children}
        </CartPageContainerMessagesContext.Provider>
    );
};

export default CartPageContainerMessagesContextWrapper;
export { CartPageContainerMessagesContext };
